import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Trash2, Edit2, Lock, Eye, EyeOff, Plus, ChevronDown, ChevronRight } from "lucide-react";

interface Library {
    id: number;
    name: string;
    date: string;
    closed: boolean;
    hidden: boolean;
    parent_id?: number;
    parent_name?: string;
    children_count: number;
    children?: Library[];
}

interface FormData {
    name: string;
    parent_id?: number | null;
    closed: boolean;
    hidden: boolean;
}

const AdminLibraries: React.FC = () => {
    const [libraries, setLibraries] = useState<Library[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState<FormData>({
        name: "",
        parent_id: null,
        closed: false,
        hidden: false,
    });
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        fetchLibraries();
    }, []);

    const fetchLibraries = async () => {
        try {
            setLoading(true);
            const response = await fetch("/api/admin/libraries");
            if (!response.ok) {
                throw new Error("Hiba a könyvtárak lekérdezése során");
            }
            const data = await response.json();
            setLibraries(data.libraries || []);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ismeretlen hiba");
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setEditingId(null);
        setFormData({
            name: "",
            parent_id: null,
            closed: false,
            hidden: false,
        });
        setIsDialogOpen(true);
    };

    const handleEditClick = (library: Library) => {
        setEditingId(library.id);
        setFormData({
            name: library.name,
            parent_id: library.parent_id || null,
            closed: library.closed,
            hidden: library.hidden,
        });
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const method = editingId ? "PATCH" : "POST";
            const url = editingId ? `/api/admin/libraries/${editingId}` : "/api/admin/libraries";

            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ library: formData }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || "Hiba a mentés során");
            }

            setIsDialogOpen(false);
            fetchLibraries();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ismeretlen hiba");
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Biztosan törlöd ezt a könyvtárat?")) return;

        try {
            const response = await fetch(`/api/admin/libraries/${id}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                throw new Error("Hiba a könyvtár törlése során");
            }

            fetchLibraries();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ismeretlen hiba");
        }
    };

    const handleClose = async (id: number) => {
        try {
            const response = await fetch(`/api/admin/libraries/${id}/close`, {
                method: "PATCH",
            });

            if (!response.ok) {
                throw new Error("Hiba a könyvtár lezárása során");
            }

            fetchLibraries();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ismeretlen hiba");
        }
    };

    const handleHide = async (id: number) => {
        try {
            const response = await fetch(`/api/admin/libraries/${id}/hide`, {
                method: "PATCH",
            });

            if (!response.ok) {
                throw new Error("Hiba a könyvtár elrejtése során");
            }

            fetchLibraries();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ismeretlen hiba");
        }
    };

    const toggleExpanded = (id: number) => {
        const newExpanded = new Set(expandedIds);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedIds(newExpanded);
    };

    const getTopLevelLibraries = () => {
        return libraries.filter(lib => !lib.parent_id);
    };

    const renderLibraryTree = (libs: Library[], depth = 0) => {
        return (
            <div className={depth > 0 ? "ml-4 border-l border-gray-200 dark:border-gray-700 pl-4" : ""}>
                {libs.map((library) => (
                    <div key={library.id} className="py-2">
                        <div className="flex items-center gap-3 p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                            {library.children && library.children.length > 0 ? (
                                <button
                                    onClick={() => toggleExpanded(library.id)}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded text-gray-900 dark:text-gray-100"
                                >
                                    {expandedIds.has(library.id) ? (
                                        <ChevronDown size={18} />
                                    ) : (
                                        <ChevronRight size={18} />
                                    )}
                                </button>
                            ) : (
                                <div className="w-6" />
                            )}

                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900 dark:text-gray-100">{library.name}</span>
                                    {library.closed && (
                                        <div className="group relative">
                                            <Lock size={16} className="text-red-500" />
                                            <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                Lezárva
                                            </div>
                                        </div>
                                    )}
                                    {library.hidden && (
                                        <div className="group relative">
                                            <EyeOff size={16} className="text-gray-500 dark:text-gray-400" />
                                            <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                Elrejtve
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {new Date(library.date).toLocaleDateString("hu-HU")} • {library.children_count} almappa
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleEditClick(library)}
                                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400"
                                    title="Szerkesztés"
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    onClick={() => handleClose(library.id)}
                                    disabled={library.closed}
                                    className={`p-2 rounded ${library.closed
                                        ? "bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                        : "hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                                        }`}
                                    title={library.closed ? "Már lezárva" : "Lezárás"}
                                >
                                    <Lock size={16} />
                                </button>
                                <button
                                    onClick={() => handleHide(library.id)}
                                    disabled={library.hidden}
                                    className={`p-2 rounded ${library.hidden
                                        ? "bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                        : "hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400"
                                        }`}
                                    title={library.hidden ? "Már elrejtve" : "Elrejtés"}
                                >
                                    <Eye size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(library.id)}
                                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400"
                                    title="Törlés"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>

                        {expandedIds.has(library.id) && library.children && library.children.length > 0 && (
                            <div className="mt-2">
                                {renderLibraryTree(library.children, depth + 1)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const getAllLibraries = () => {
        const flatList: Library[] = [];
        const traverse = (libs: Library[]) => {
            libs.forEach((lib) => {
                flatList.push(lib);
                if (lib.children) traverse(lib.children);
            });
        };
        traverse(libraries);
        return flatList;
    };

    if (loading) {
        return (
            <div className="p-6">
                <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Könyvtárak</h1>
                <p className="text-gray-600 dark:text-gray-400">Betöltés...</p>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Könyvtárak</h1>
                <Button onClick={handleAddClick} className="gap-2">
                    <Plus size={18} />
                    Új könyvtár
                </Button>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                    {error}
                </div>
            )}

            {libraries.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">Még nincsenek könyvtárak.</p>
            ) : (
                renderLibraryTree(getTopLevelLibraries())
            )}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingId ? "Könyvtár szerkesztése" : "Új könyvtár"}
                        </DialogTitle>
                        <DialogDescription>
                            {editingId
                                ? "Módosítsd a könyvtár adatait alább."
                                : "Hozz létre egy új virtuális könyvtárat a képek szervezéséhez."}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="name">Név *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({ ...formData, name: e.target.value })
                                }
                                placeholder="Könyvtár neve"
                            />
                        </div>

                        <div>
                            <Label htmlFor="parent">Szülőkönyvtár (opcionális)</Label>
                            <Select
                                value={formData.parent_id?.toString() || "none"}
                                onValueChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        parent_id: value === "none" ? null : parseInt(value),
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Válassz szülőkönyvtárat..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Nincs szülőkönyvtár</SelectItem>
                                    {getAllLibraries()
                                        .filter((lib) => lib.id !== editingId)
                                        .map((lib) => (
                                            <SelectItem key={lib.id} value={lib.id.toString()}>
                                                {lib.name}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">Max 3 szint mély lehet</p>
                        </div>

                        <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                    checked={formData.closed}
                                    onCheckedChange={(checked) =>
                                        setFormData({ ...formData, closed: checked as boolean })
                                    }
                                />
                                <span className="text-sm">Lezárva</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                    checked={formData.hidden}
                                    onCheckedChange={(checked) =>
                                        setFormData({ ...formData, hidden: checked as boolean })
                                    }
                                />
                                <span className="text-sm">Elrejtve</span>
                            </label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsDialogOpen(false)}
                        >
                            Mégsem
                        </Button>
                        <Button onClick={handleSave} disabled={!formData.name.trim()}>
                            Mentés
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminLibraries;
